import React, { useState, useRef, useEffect, memo } from 'react';
import {
  Copy,
  Bookmark,
  Edit2,
  Trash2,
  Check,
  ThumbsUp,
  ThumbsDown,
  RefreshCw,
  Send,
  X,
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

interface Message {
  id: string;
  text: string;
  isUser: boolean;
  is_saved?: boolean;
  user_rating?: number | null;
}

interface MessageActionsProps {
  message: Message;
  onCopy: () => void;
  onSave?: () => void;
  onEdit?: () => void;
  onDelete: () => void;
  onRate?: (rating: 1 | 5, feedback?: string) => void;
  onRegenerate?: () => void;
}

const MessageActions: React.FC<MessageActionsProps> = memo(
  ({ message, onCopy, onSave, onEdit, onDelete, onRate, onRegenerate }) => {
    const { theme } = useTheme();
    const [showCopied, setShowCopied] = useState(false);
    const [showSaved, setShowSaved] = useState(false);
    const [showFeedback, setShowFeedback] = useState(false);
    const [feedbackText, setFeedbackText] = useState('');
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
      if (showFeedback && textareaRef.current) {
        textareaRef.current.focus();
      }
    }, [showFeedback]);

    const handleCopy = () => {
      onCopy();
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 1500);
    };

    const handleSave = () => {
      if (onSave) {
        onSave();
        setShowSaved(true);
        setTimeout(() => setShowSaved(false), 1500);
      }
    };

    const handleThumbsDown = () => {
      if (!onRate) return;
      // If already rated thumbs down, toggle it off
      if (message.user_rating === 1) {
        onRate(1);
        return;
      }
      // Show feedback prompt
      setShowFeedback(true);
    };

    const submitFeedback = () => {
      if (onRate) {
        onRate(1, feedbackText.trim() || undefined);
      }
      setShowFeedback(false);
      setFeedbackText('');
    };

    const skipFeedback = () => {
      if (onRate) {
        onRate(1);
      }
      setShowFeedback(false);
      setFeedbackText('');
    };

    const handleFeedbackKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        submitFeedback();
      }
      if (e.key === 'Escape') {
        skipFeedback();
      }
    };

    return (
      <div className="flex flex-col items-start gap-1">
        <div className="flex items-center justify-start">
          {/* Primary actions group */}
          <div className="flex items-center space-x-1">
            {/* Copy button with feedback animation */}
            <button
              onClick={handleCopy}
              className={`p-1.5 rounded-lg transition-all duration-200 ${theme.colors.text.secondary} hover:${theme.colors.bg.tertiary} hover:${theme.colors.text.primary} hover:scale-105 active:scale-95`}
              title="Copiar mensaje"
              aria-label="Copiar mensaje"
            >
              {showCopied ? (
                <Check className="w-3.5 h-3.5 text-nb-success animate-in fade-in duration-200" />
              ) : (
                <Copy className="w-3.5 h-3.5" />
              )}
            </button>

            {/* Thumbs up/down - Only for AI messages */}
            {!message.isUser && onRate && (
              <>
                <button
                  onClick={() => onRate(5)}
                  className={`p-1.5 rounded-lg transition-all duration-200 hover:${
                    theme.colors.bg.tertiary
                  } hover:scale-105 active:scale-95 ${
                    message.user_rating === 5
                      ? 'text-emerald-500'
                      : `${theme.colors.text.secondary} hover:text-emerald-500`
                  }`}
                  title="Buena respuesta"
                  aria-label="Marcar como buena respuesta"
                >
                  <ThumbsUp
                    className={`w-3.5 h-3.5 ${message.user_rating === 5 ? 'fill-current' : ''}`}
                  />
                </button>
                <button
                  onClick={handleThumbsDown}
                  className={`p-1.5 rounded-lg transition-all duration-200 hover:${
                    theme.colors.bg.tertiary
                  } hover:scale-105 active:scale-95 ${
                    message.user_rating === 1
                      ? 'text-red-400'
                      : `${theme.colors.text.secondary} hover:text-red-400`
                  }`}
                  title="Mala respuesta"
                  aria-label="Marcar como mala respuesta"
                >
                  <ThumbsDown
                    className={`w-3.5 h-3.5 ${message.user_rating === 1 ? 'fill-current' : ''}`}
                  />
                </button>
              </>
            )}

            {/* Regenerate button - Only for AI messages */}
            {!message.isUser && onRegenerate && (
              <button
                onClick={onRegenerate}
                className={`p-1.5 rounded-lg transition-all duration-200 ${theme.colors.text.secondary} hover:${theme.colors.bg.tertiary} hover:text-nb-accent hover:scale-105 active:scale-95`}
                title="Regenerar respuesta"
                aria-label="Regenerar respuesta"
              >
                <RefreshCw className="w-3.5 h-3.5" />
              </button>
            )}

            {/* Save/Unsave button */}
            {onSave && (
              <button
                onClick={handleSave}
                className={`p-1.5 rounded-lg transition-all duration-200 hover:${
                  theme.colors.bg.tertiary
                } hover:scale-105 active:scale-95 ${
                  message.is_saved
                    ? `${theme.colors.text.secondary} hover:${theme.colors.text.primary} opacity-70`
                    : `${theme.colors.text.secondary} hover:${theme.colors.text.primary}`
                }`}
                title={message.is_saved ? 'Quitar de guardados' : 'Guardar en Drive'}
                aria-label={message.is_saved ? 'Quitar de guardados' : 'Guardar en Drive'}
              >
                <Bookmark
                  className={`w-3.5 h-3.5 transition-transform duration-200 ${
                    message.is_saved || showSaved ? 'fill-current scale-110' : ''
                  }`}
                />
              </button>
            )}

            {/* Edit button - Only for user messages */}
            {message.isUser && onEdit && (
              <button
                onClick={onEdit}
                className={`p-1.5 rounded-lg transition-all duration-200 ${theme.colors.text.secondary} hover:${theme.colors.bg.tertiary} hover:text-nb-accent hover:scale-105 active:scale-95`}
                title="Editar mensaje"
                aria-label="Editar mensaje"
              >
                <Edit2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Destructive action - separated with gap */}
          <div className="ml-3 pl-3 border-l border-white/10">
            <button
              onClick={onDelete}
              className={`p-1.5 rounded-lg transition-all duration-200 ${theme.colors.text.secondary} opacity-50 hover:opacity-100 hover:${theme.colors.bg.tertiary} hover:text-nb-error hover:scale-105 active:scale-95`}
              title="Eliminar mensaje"
              aria-label="Eliminar mensaje"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Feedback textarea — appears below actions when thumbs down clicked */}
        {showFeedback && (
          <div
            className={`flex items-center gap-1.5 w-64 mt-1 px-2 py-1.5 rounded-lg ${theme.colors.bg.secondary} border ${theme.colors.border.primary} animate-in fade-in slide-in-from-top-1 duration-150`}
          >
            <textarea
              ref={textareaRef}
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              onKeyDown={handleFeedbackKeyDown}
              placeholder="¿Qué estuvo mal? (opcional)"
              aria-label="Feedback sobre la respuesta"
              rows={1}
              className={`flex-1 text-xs bg-transparent ${theme.colors.text.primary} placeholder:${theme.colors.text.secondary} resize-none outline-none`}
            />
            <button
              onClick={submitFeedback}
              className="p-1 rounded text-nb-accent hover:bg-nb-accent/10 transition-colors"
              title="Enviar feedback"
              aria-label="Enviar feedback"
            >
              <Send className="w-3 h-3" />
            </button>
            <button
              onClick={skipFeedback}
              className={`p-1 rounded ${theme.colors.text.secondary} hover:${theme.colors.text.primary} transition-colors`}
              title="Omitir"
              aria-label="Omitir feedback"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>
    );
  }
);

MessageActions.displayName = 'MessageActions';

export default MessageActions;
